import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import styles from './AppointmentFormModal.module.css';
import { Add, Delete, ChevronLeft, ChevronRight, Close, Today, PersonAdd, CheckCircle, Payment as PaymentIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AppointmentFormData, AppointmentServiceFormData } from '../types/forms';
import { AppointmentStatus, Client, Hairdresser, ServiceTemplate, Payment, Service, Timeoff } from '../types/entities';
import ServiceTemplateSelector from './ServiceTemplateSelector';
import ClientFormModal from './ClientFormModal';
import FinalizeAppointmentModal from './FinalizeAppointmentModal';
import { ClientFormData } from '../types/forms';
import { getAppointmentChanges } from '../utils/changeDetection';
import { useAppointmentsApi } from '../hooks/useAppointmentsApi';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventInput } from '@fullcalendar/core';
import plLocale from '@fullcalendar/core/locales/pl';

// Timeoff event styling
const timeoffStyles = `
  .fc-event.timeoff-event {
    background: linear-gradient(45deg, rgba(255, 152, 0, 0.3) 25%, rgba(245, 124, 0, 0.3) 25%, rgba(245, 124, 0, 0.3) 50%, rgba(255, 152, 0, 0.3) 50%, rgba(255, 152, 0, 0.3) 75%, rgba(245, 124, 0, 0.3) 75%, rgba(245, 124, 0, 0.3)) !important;
    background-size: 8px 8px !important;
    border: 1px solid rgba(230, 81, 0, 0.4) !important;
    border-radius: 4px !important;
  }
`;

interface AppointmentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data?: AppointmentFormData | Partial<AppointmentFormData> | any) => void;
  formData: AppointmentFormData;
  onInputChange: (field: keyof AppointmentFormData) => (event: any) => void;
  onServiceChange: (index: number, field: keyof AppointmentServiceFormData, value: any) => void;
  onAddService: (serviceTemplate: ServiceTemplate) => void;
  onRemoveService: (index: number) => void;
  onCreateClient?: (searchText?: string) => void; // Callback to create a new client
  onUpdateClient?: (id: number, data: Partial<ClientFormData>) => Promise<Client>; // Callback to update a client
  onRefreshAppointments?: () => void; // Callback to refresh appointments list
  title: string;
  submitButtonText: string;
  clients: Client[];
  hairdressers: Hairdresser[];
  serviceTemplates: ServiceTemplate[];
  timeoffs?: Timeoff[];
  appointments?: any[];
  isEdit?: boolean;
  editingAppointment?: any; // The appointment being edited
}

const AppointmentFormModal: React.FC<AppointmentFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  onServiceChange,
  onAddService,
  onRemoveService,
  onCreateClient,
  onUpdateClient,
  onRefreshAppointments,
  title,
  submitButtonText,
  clients,
  hairdressers,
  serviceTemplates,
  timeoffs = [],
  appointments = [],
  isEdit = false,
  editingAppointment,
}) => {
  const { t } = useTranslation();
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [clientSearchText, setClientSearchText] = useState<string>('');
  const [calendarView, setCalendarView] = useState<string>('timeGridWeek');
  const [originalFormData, setOriginalFormData] = useState<AppointmentFormData | null>(null);
  const [clientNotes, setClientNotes] = useState<string>('');
  const [originalClientNotes, setOriginalClientNotes] = useState<string>('');
  const [originalAppointmentNotes, setOriginalAppointmentNotes] = useState<string>('');
  const [finalizeModalOpen, setFinalizeModalOpen] = useState<boolean>(false);
  const [localAppointments, setLocalAppointments] = useState<any[]>(appointments);
  const [calendarRange, setCalendarRange] = useState<{ start: Date; end: Date } | null>(null);
  const [timeoffConflict, setTimeoffConflict] = useState<boolean>(false);
  const calendarRef = useRef<FullCalendar>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { finalizeAppointment, updateAppointment, fetchAppointments } = useAppointmentsApi();

  // Sync local appointments with props
  useEffect(() => {
    setLocalAppointments(appointments);
  }, [appointments]);

  useEffect(() => {
    if (!open || !calendarRange) return;

    let isCancelled = false;

    const loadAppointmentsForRange = async () => {
      try {
        // Add a 1-day buffer on both sides to avoid missing boundary appointments
        const bufferStart = new Date(calendarRange.start);
        bufferStart.setDate(bufferStart.getDate() - 1);
        const bufferEnd = new Date(calendarRange.end);
        bufferEnd.setDate(bufferEnd.getDate() + 1);

        const response = await fetchAppointments({
          dateFrom: bufferStart.toISOString().split('T')[0],
          dateTo: bufferEnd.toISOString().split('T')[0],
          limit: 0,
        });

        if (isCancelled) return;

        const fetchedAppointments = (response as any)?.data ?? (response as any)?.items ?? [];
        setLocalAppointments(fetchedAppointments);
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to fetch appointments for calendar range:', error);
        }
      } finally {
        // No-op for now; reserved for future loading indicators
      }
    };

    loadAppointmentsForRange();

    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, calendarRange?.start, calendarRange?.end]);

  // Check for timeoff conflicts
  const checkTimeoffConflict = useCallback(() => {
    if (!formData.date || !formData.hairdresser_id) {
      setTimeoffConflict(false);
      return;
    }

    const appointmentStart = new Date(formData.date);
    const totalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
    const appointmentEnd = new Date(appointmentStart.getTime() + totalDuration * 60000);

    const hasConflict = timeoffs.some(timeoff => {
      if (timeoff.hairdresser?.id !== formData.hairdresser_id) return false;
      
      const timeoffStart = new Date(timeoff.startDate);
      const timeoffEnd = new Date(timeoff.endDate);
      
      // Check if appointment overlaps with timeoff
      return appointmentStart < timeoffEnd && appointmentEnd > timeoffStart;
    });

    setTimeoffConflict(hasConflict);
  }, [formData.date, formData.hairdresser_id, formData.services, timeoffs]);

  // Check for conflicts when form data changes
  useEffect(() => {
    checkTimeoffConflict();
  }, [checkTimeoffConflict]);

  // Update local appointments when service durations or status change in the form
  useEffect(() => {
    if (isEdit && editingAppointment && formData.services) {
      // Calculate new total duration
      const newTotalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
      
      // Update the local appointment with new service durations but keep original status for calendar
      setLocalAppointments(prev => 
        prev.map(apt => 
          apt.id === editingAppointment.id 
            ? { 
                ...apt, 
                // Don't update status here - keep original for calendar display
                services: formData.services.map((service, index) => {
                  const serviceWithId = service as any;
                  return {
                    ...serviceWithId,
                    id: serviceWithId.id || Math.random(),
                  };
                })
              }
            : apt
        )
      );
    }
  }, [formData.services, formData.status, isEdit, editingAppointment]);

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  // Handle finalize appointment
  const handleFinalizeAppointment = () => {
    setFinalizeModalOpen(true);
  };

  const handleFinalizeModalClose = () => {
    setFinalizeModalOpen(false);
  };

  const handleFinalize = async (payments: any[], discount?: any, serviceDiscounts?: any[]) => {
    try {
      const finalizedAppointment = await finalizeAppointment(editingAppointment.id, payments, discount, serviceDiscounts);
      console.log('Finalized appointment received:', finalizedAppointment);
      console.log('Services in finalized appointment:', finalizedAppointment.services?.map(s => ({ name: s.name, price: s.price, duration: s.duration })));
      toast.success('Appointment finalized successfully!');
      setFinalizeModalOpen(false);
      
      // Update the parent component with the finalized appointment data
      if (onSubmit) {
        onSubmit(finalizedAppointment as any);
      }
      
      onClose(); // Close the main appointment modal
    } catch (error) {
      console.error('Error finalizing appointment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to finalize appointment');
    }
  };

  // Handle client notes change (no auto-save)
  const handleClientNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClientNotes(event.target.value);
  };

  const shouldShowError = (fieldName: string, hasError: boolean) => {
    return touchedFields.has(fieldName) && hasError;
  };

  // Store original form data when editing
  useEffect(() => {
    if (open && isEdit && editingAppointment && !originalFormData) {
      setOriginalFormData({ ...formData });
      setOriginalAppointmentNotes(formData.notes || '');
    } else if (!open) {
      setOriginalFormData(null);
      setOriginalAppointmentNotes('');
    }
  }, [open, isEdit, editingAppointment, formData, originalFormData]);

  // Initialize client notes when client is selected
  useEffect(() => {
    if (formData.client_id > 0) {
      const selectedClient = clients.find(client => client.id === formData.client_id);
      const notes = selectedClient?.notes || '';
      setClientNotes(notes);
      setOriginalClientNotes(notes);
    } else {
      setClientNotes('');
      setOriginalClientNotes('');
    }
  }, [formData.client_id, clients]);

  const isFormValid = useMemo(() => {
    return (
      formData.client_id > 0 &&
      formData.hairdresser_id > 0 &&
      formData.date.trim() !== '' &&
      formData.services.length > 0 &&
      formData.services.every(service => 
        service.name.trim() !== '' && 
        service.price > 0 && 
        service.duration > 0
      )
    );
  }, [formData.client_id, formData.hairdresser_id, formData.date, formData.services]);

  // Check if form data has changed from original
  const hasFormDataChanged = useMemo(() => {
    if (!isEdit || !originalFormData) {
      return true; // For new appointments, always allow submission
    }

    // Check if client notes have changed
    if (clientNotes !== originalClientNotes) {
      return true;
    }

    // Compare basic fields
    if (
      formData.client_id !== originalFormData.client_id ||
      formData.hairdresser_id !== originalFormData.hairdresser_id ||
      formData.date !== originalFormData.date ||
      formData.status !== originalFormData.status ||
      formData.notes !== originalFormData.notes
    ) {
      return true;
    }

    // Compare services
    if (formData.services.length !== originalFormData.services.length) {
      return true;
    }

    for (let i = 0; i < formData.services.length; i++) {
      const currentService = formData.services[i];
      const originalService = originalFormData.services[i];
      
      if (
        currentService.name !== originalService.name ||
        currentService.description !== originalService.description ||
        currentService.price !== originalService.price ||
        currentService.duration !== originalService.duration
      ) {
        return true;
      }
    }

    return false;
  }, [isEdit, originalFormData, clientNotes, originalClientNotes, formData]);


  const shouldDisableInputs = useMemo(() => {
    if (!isEdit) return false;
    if (originalFormData?.status === AppointmentStatus.COMPLETED) return true;
    if (originalFormData?.status === AppointmentStatus.CANCELED) return true;
    return false;
  }, [isEdit, originalFormData?.status]);

  const shouldDisableDateAndClient = useMemo(() => {
    if (!isEdit) return false;
    if (originalFormData?.status === AppointmentStatus.COMPLETED) return true;
    if (originalFormData?.status === AppointmentStatus.CANCELED) return true;
    // Allow editing date and client for NEW and CONFIRMED appointments
    return false;
  }, [isEdit, originalFormData?.status]);

  // Check if notes have changed (for completed appointments)
  const haveNotesChanged = useMemo(() => {
    if (!isEdit || !shouldDisableInputs) {
      return false;
    }
    
    const clientNotesChanged = clientNotes !== originalClientNotes;
    const appointmentNotesChanged = (formData.notes || '') !== originalAppointmentNotes;
    return clientNotesChanged || appointmentNotesChanged;
  }, [isEdit, shouldDisableInputs, clientNotes, originalClientNotes, formData.notes, originalAppointmentNotes]);

  // Handle notes-only update for completed appointments
  const handleNotesUpdate = async () => {
    try {
      let clientNotesUpdated = false;
      let appointmentNotesUpdated = false;

      // Update client notes if changed
      if (clientNotes !== originalClientNotes && onUpdateClient && formData.client_id > 0) {
        await onUpdateClient(formData.client_id, { notes: clientNotes });
        setOriginalClientNotes(clientNotes);
        clientNotesUpdated = true;
      }

      // Update appointment notes if changed
      if ((formData.notes || '') !== originalAppointmentNotes && editingAppointment) {
        await updateAppointment(editingAppointment.id, { notes: formData.notes });
        setOriginalAppointmentNotes(formData.notes || '');
        appointmentNotesUpdated = true;
      }

      // Show appropriate toast messages
      if (clientNotesUpdated && appointmentNotesUpdated) {
        toast.success(t('appointments.clientAndAppointmentNotesUpdated'));
      } else if (clientNotesUpdated) {
        toast.success(t('appointments.clientNotesUpdated'));
      } else if (appointmentNotesUpdated) {
        toast.success(t('appointments.appointmentNotesUpdated'));
      }

      onClose(); // Close the dialog after successful update
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error(t('appointments.failedToUpdateNotes'));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isFormValid) {
      try {
        // First, update client notes if they have changed
        if (clientNotes !== originalClientNotes && onUpdateClient && formData.client_id > 0) {
          await onUpdateClient(formData.client_id, { notes: clientNotes });
          setOriginalClientNotes(clientNotes);
        }

        // Then submit the appointment
        if (isEdit && originalFormData) {
          // For edit mode, only send changed fields
          const changedData = getAppointmentChanges(originalFormData, formData);
          onSubmit(changedData);
        } else {
          // For create mode, send all data
          onSubmit(formData);
        }
      } catch (error) {
        console.error('Failed to update client notes:', error);
        toast.error(t('appointments.failedToUpdateClientNotes'));
      }
    }
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const calculateFinishTimeForInput = (): string => {
    if (!formData.date || formData.services.length === 0) {
      return '';
    }
    
    // Handle UTC date from database and convert to local timezone
    const startDate = new Date(formData.date);
    const totalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
    const finishDate = new Date(startDate.getTime() + totalDuration * 60000);
    
    const year = finishDate.getFullYear();
    const month = String(finishDate.getMonth() + 1).padStart(2, '0');
    const day = String(finishDate.getDate()).padStart(2, '0');
    const hours = String(finishDate.getHours()).padStart(2, '0');
    const minutes = String(finishDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to check if appointment should scroll based on visibility
  const shouldScrollToAppointment = (appointmentDate: Date, totalDuration: number): boolean => {
    const appointmentEndTime = new Date(appointmentDate.getTime() + totalDuration * 60000);
    
    // Check if calendar is available and get its view
    if (!calendarRef.current) {
      return true;
    }
    
    const calendar = calendarRef.current.getApi();
    
    // Try to get view info, but handle potential errors
    try {
      const view = calendar.view;
      
      // For timeGrid views (both week and day), we need to get the actual visible time range
      let visibleStart, visibleEnd;
      
      if (view.type === 'timeGridWeek' || view.type === 'timeGridDay') {
        // Try to find the calendar DOM element using a different approach
        const calendarElement = document.querySelector('.fc') || 
                               document.querySelector('[data-fullcalendar]');
        
        let scrollContainer = null;
        if (calendarElement) {
          scrollContainer = calendarElement.querySelector('.fc-scrollgrid-sync-table') ||
                           calendarElement.querySelector('.fc-scroller') ||
                           calendarElement.querySelector('.fc-scrollgrid') ||
                           calendarElement.querySelector('[class*="scroll"]');
        }
        
        if (scrollContainer) {
          const scrollTop = scrollContainer.scrollTop;
          const containerHeight = scrollContainer.clientHeight;
          
          // Calculate visible time range based on scroll position
          // Each hour is approximately 60px in FullCalendar
          const hoursFromTop = scrollTop / 60;
          const visibleHours = containerHeight / 60;
          
          const startOfDay = new Date(appointmentDate);
          startOfDay.setHours(0, 0, 0, 0);
          
          visibleStart = new Date(startOfDay.getTime() + hoursFromTop * 60 * 60 * 1000);
          visibleEnd = new Date(startOfDay.getTime() + (hoursFromTop + visibleHours) * 60 * 60 * 1000);
          } else {
          // Fallback: use activeStart/activeEnd
          visibleStart = view.activeStart;
          visibleEnd = view.activeEnd;
        }
      } else {
        // For other views (like dayGridMonth), use activeStart/activeEnd
        visibleStart = view.activeStart;
        visibleEnd = view.activeEnd;
      }
            
       // Check if appointment start and end are visible
       const appointmentStartIsVisible = appointmentDate >= visibleStart && appointmentDate <= visibleEnd;
       const appointmentEndIsVisible = appointmentEndTime >= visibleStart && appointmentEndTime <= visibleEnd;
       
       
       // If either start or end is not visible, we need to scroll
       if (!appointmentStartIsVisible || !appointmentEndIsVisible) {
         return true;
       }
       
       // Calculate how much time is visible before and after appointment
       const timeBeforeAppointment = appointmentDate.getTime() - visibleStart.getTime();
       const timeAfterAppointment = visibleEnd.getTime() - appointmentEndTime.getTime();
       const oneHourInMs = 60 * 60 * 1000;

       const shouldScroll = timeBeforeAppointment < oneHourInMs && timeAfterAppointment < oneHourInMs;      
      return shouldScroll;
    } catch (error) {
      return true;
    }
  };

  // Handle scrolling when editing an existing appointment
  useEffect(() => {
    if (open && isEdit && editingAppointment && formData.date) {
      const appointmentDate = new Date(formData.date);
      const totalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
      
      // Use multiple timeouts to ensure calendar is fully loaded
      const navigateToAppointment = () => {
        const calendar = calendarRef.current?.getApi();
        if (calendar) {
          console.log('Navigating to appointment date:', appointmentDate);
          
          // Navigate to the appointment's date first
          calendar.gotoDate(appointmentDate);
          
          // Refresh calendar events to ensure correct colors
          calendar.refetchEvents();
          
          // Then scroll to the appointment time with 30-minute margin
          if (shouldScrollToAppointment(appointmentDate, totalDuration)) {
            const hours = appointmentDate.getHours();
            const minutes = appointmentDate.getMinutes();
            
            // Calculate scroll time: 30 minutes before appointment
            let scrollHours = hours;
            let scrollMinutes = minutes - 30; // 30-minute margin
            
            // Handle minute underflow
            if (scrollMinutes < 0) {
              scrollMinutes += 60;
              scrollHours -= 1;
            }
            
            // Ensure we don't go below 8 AM (calendar start time)
            if (scrollHours < 8) {
              scrollHours = 8;
              scrollMinutes = 0;
            }
            
            const timeStr = `${scrollHours.toString().padStart(2, '0')}:${scrollMinutes.toString().padStart(2, '0')}`;
            console.log('Scrolling to time with 30-min margin:', timeStr, 'for appointment at', `${hours}:${minutes.toString().padStart(2, '0')}`);
            calendar.scrollToTime(timeStr);
          }
        } else {
          console.log('Calendar not ready yet, retrying...');
          // Retry after a short delay if calendar is not ready
          setTimeout(navigateToAppointment, 100);
        }
      };
      
      // Initial delay to ensure calendar is mounted
      setTimeout(navigateToAppointment, 300);
    }
  }, [open, isEdit, editingAppointment, formData.date, formData.services, editingAppointment?.status]);

  const calculateTotalPrice = (): number => {
    if (isEdit && editingAppointment?.services) {
      // For edit mode, calculate from the actual appointment services
      const total = editingAppointment.services.reduce((total: number, service: any) => total + (parseFloat(service.price) || 0), 0);
      console.log('Calculating total price from editingAppointment.services:', editingAppointment.services.map((s: any) => ({ name: s.name, price: s.price, parsedPrice: parseFloat(s.price) })), 'Total:', total);
      return total;
    }
    // For create mode, calculate from form data
    const total = formData.services.reduce((total, service) => total + service.price, 0);
    console.log('Calculating total price from formData.services:', formData.services, 'Total:', total);
    return total;
  };

  const calculateTotalDuration = (): number => {
    if (isEdit && editingAppointment?.services) {
      // For edit mode, calculate from the actual appointment services
      return editingAppointment.services.reduce((total: number, service: any) => total + service.duration, 0);
    }
    // For create mode, calculate from form data
    return formData.services.reduce((total, service) => total + service.duration, 0);
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    } else if (mins === 0) {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')}`;
    } else {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')} ${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    }
  };

  const calculateFinishTime = (): string => {
    if (!formData.date || formData.services.length === 0) {
      return '';
    }
    
    // Handle UTC date from database and convert to local timezone
    const startDate = new Date(formData.date);
    const totalDurationMinutes = calculateTotalDuration();
    const finishDate = new Date(startDate.getTime() + totalDurationMinutes * 60000);
    
    // Format as dd.mm.yyyy, hh:mm
    const day = String(finishDate.getDate()).padStart(2, '0');
    const month = String(finishDate.getMonth() + 1).padStart(2, '0');
    const year = finishDate.getFullYear();
    const hours = String(finishDate.getHours()).padStart(2, '0');
    const minutes = String(finishDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  // Convert appointments to calendar events for display
  // Include all appointments, including the one being edited
  const calendarEvents: EventInput[] = localAppointments
    .map(appointment => {
      // Check if this is the appointment being edited
      const isBeingEdited = isEdit && editingAppointment && appointment.id === editingAppointment.id;
      
      // Use form data for the appointment being edited, otherwise use original appointment data
      const currentDate = isBeingEdited ? formData.date : appointment.date;
      const currentServices = isBeingEdited ? formData.services : appointment.services;
      
      const startDate = new Date(currentDate);
      const duration = currentServices?.reduce((total: number, service: any) => total + service.duration, 0) || 60;
      const endDate = new Date(startDate.getTime() + duration * 60000);

      // Get color based on appointment status (for non-edited appointments)
      const getEventColor = (status: AppointmentStatus): string => {
        const colors = {
          [AppointmentStatus.NEW]: '#1976d2', // Primary blue
          [AppointmentStatus.CONFIRMED]: '#9c27b0', // Purple
          [AppointmentStatus.COMPLETED]: '#2e7d32', // Success green
          [AppointmentStatus.CANCELED]: '#d32f2f', // Error red
        };
        return colors[status] || '#1976d2';
      };

      // Get status-based colors from CSS variables
      const getStatusColors = (status: AppointmentStatus) => {
        const getCSSVariable = (variableName: string) => {
          return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
        };

        const statusColors = {
          [AppointmentStatus.NEW]: {
            backgroundColor: getCSSVariable('--appointment-new-bg'),
            borderColor: getCSSVariable('--appointment-new-border'),
            textColor: getCSSVariable('--appointment-new-text')
          },
          [AppointmentStatus.CONFIRMED]: {
            backgroundColor: getCSSVariable('--appointment-confirmed-bg'),
            borderColor: getCSSVariable('--appointment-confirmed-border'),
            textColor: getCSSVariable('--appointment-confirmed-text')
          },
          [AppointmentStatus.COMPLETED]: {
            backgroundColor: getCSSVariable('--appointment-completed-bg'),
            borderColor: getCSSVariable('--appointment-completed-border'),
            textColor: getCSSVariable('--appointment-completed-text')
          },
          [AppointmentStatus.CANCELED]: {
            backgroundColor: getCSSVariable('--appointment-canceled-bg'),
            borderColor: getCSSVariable('--appointment-canceled-border'),
            textColor: getCSSVariable('--appointment-canceled-text')
          },
        };
        return statusColors[status] || statusColors[AppointmentStatus.NEW];
      };

      // Use form status for the appointment being edited, otherwise use original appointment status
      const currentStatus = isBeingEdited ? formData.status : appointment.status;
      const colors = isBeingEdited ? getStatusColors(currentStatus) : getStatusColors(AppointmentStatus.NEW);
      
      // Create services text for the title (only for non-editing appointments)
      const servicesText = !isBeingEdited && currentServices && currentServices.length > 0 
        ? ` - ${currentServices.map((service: any) => service.name).join(', ')}`
        : '';
      
      return {
        id: `${appointment.id}-${appointment.status}`, // Include status in ID to force refresh
        title: `${appointment.client?.name || t('calendar.unknownClient')}${servicesText}${isBeingEdited ? ` (${t('appointments.editing')})` : ''}`,
        start: startDate,
        end: endDate,
        backgroundColor: isBeingEdited ? colors.backgroundColor : getEventColor(currentStatus),
        borderColor: isBeingEdited ? colors.borderColor : getEventColor(currentStatus),
        textColor: isBeingEdited ? colors.textColor : 'white',
        display: 'block',
        extendedProps: {
          appointment: appointment,
        },
        // Calendar is read-only, no editing properties needed
        // Add editing class for styling and status-based class
        classNames: [
          ...(isBeingEdited ? ['appointment-being-edited'] : []),
          ...(isBeingEdited ? [`appointment-status-${currentStatus.toLowerCase()}`] : ['appointment-event'])
        ],
      };
    });

  // Create placeholder for appointment preview (only for new appointments)
  const createAppointmentPreview = (): EventInput | null => {
    // Only show preview for new appointments, not when editing
    if (isEdit || !formData.date || formData.services.length === 0) {
      return null;
    }

    const startDate = new Date(formData.date);
    const totalDuration = calculateTotalDuration();
    const endDate = new Date(startDate.getTime() + totalDuration * 60000);

    // Get client name - show placeholder if no client selected
    const selectedClient = clients.find(client => client.id === formData.client_id);
    const clientName = selectedClient?.name || 'Select Client';

    const hasClient = formData.client_id > 0;
    const titlePrefix = hasClient ? t('appointments.new') : t('appointments.preview');
    
    // Get status-based class name and colors
    const getStatusClass = (status: AppointmentStatus): string => {
      const statusClasses = {
        [AppointmentStatus.NEW]: 'appointment-status-new',
        [AppointmentStatus.CONFIRMED]: 'appointment-status-confirmed',
        [AppointmentStatus.COMPLETED]: 'appointment-status-completed',
        [AppointmentStatus.CANCELED]: 'appointment-status-canceled',
      };
      return statusClasses[status] || 'appointment-status-new';
    };

    // Get status-based colors from CSS variables
    const getStatusColors = (status: AppointmentStatus) => {
      const getCSSVariable = (variableName: string) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
      };

      const statusColors = {
        [AppointmentStatus.NEW]: {
          backgroundColor: getCSSVariable('--appointment-new-bg'),
          borderColor: getCSSVariable('--appointment-new-border'),
          textColor: getCSSVariable('--appointment-new-text')
        },
        [AppointmentStatus.CONFIRMED]: {
          backgroundColor: getCSSVariable('--appointment-confirmed-bg'),
          borderColor: getCSSVariable('--appointment-confirmed-border'),
          textColor: getCSSVariable('--appointment-confirmed-text')
        },
        [AppointmentStatus.COMPLETED]: {
          backgroundColor: getCSSVariable('--appointment-completed-bg'),
          borderColor: getCSSVariable('--appointment-completed-border'),
          textColor: getCSSVariable('--appointment-completed-text')
        },
        [AppointmentStatus.CANCELED]: {
          backgroundColor: getCSSVariable('--appointment-canceled-bg'),
          borderColor: getCSSVariable('--appointment-canceled-border'),
          textColor: getCSSVariable('--appointment-canceled-text')
        },
      };
      return statusColors[status] || statusColors[AppointmentStatus.NEW];
    };

    const colors = getStatusColors(formData.status);

    return {
      id: 'new-appointment-preview',
      title: `${clientName} (${titlePrefix})`,
      start: startDate,
      end: endDate,
      backgroundColor: colors.backgroundColor,
      borderColor: colors.borderColor,
      textColor: colors.textColor,
      display: 'block',
      classNames: [
        'new-appointment-preview', 
        getStatusClass(formData.status),
        hasClient ? 'appointment-with-client' : 'appointment-preview-only'
      ],
      // Make preview non-editable
      editable: false,
      startEditable: false,
      durationEditable: false,
    };
  };

  const appointmentPreview = createAppointmentPreview();
  // Convert timeoffs to calendar events - only show timeoffs for the selected hairdresser
  const timeoffEvents: EventInput[] = timeoffs
    .filter(timeoff => timeoff.hairdresser?.id === formData.hairdresser_id)
    .map(timeoff => {
      return {
        id: `timeoff-${timeoff.id}`,
        title: `🚫 ${timeoff.hairdresser?.name || t('common.notAvailable')}`,
        start: new Date(timeoff.startDate),
        end: new Date(timeoff.endDate),
        backgroundColor: 'rgba(255, 152, 0, 0.3)',
        borderColor: 'rgba(230, 81, 0, 0.4)',
        textColor: 'rgba(0, 0, 0, 0.7)',
        extendedProps: {
          timeoff: timeoff,
          type: 'timeoff',
        },
        display: 'block',
        classNames: ['timeoff-event'],
        editable: false,
        startEditable: false,
        durationEditable: false,
      };
    });

  const allCalendarEvents = appointmentPreview 
    ? [...calendarEvents, ...timeoffEvents, appointmentPreview]
    : [...calendarEvents, ...timeoffEvents];

  // Handle calendar date click
  const handleCalendarDateClick = (dateClickInfo: any) => {
    // Only allow date changes for new appointments
    if (formData.status !== AppointmentStatus.NEW) {
      return;
    }
    
    const clickedDate = dateClickInfo.date;
    const dateStr = clickedDate.toISOString();
    
    // Update form with selected date and time (minutes set to 0)
    onInputChange('date')({ target: { value: dateStr } });
    
    // Navigate calendar to the selected date and scroll to time
    if (calendarRef.current) {
      setTimeout(() => {
        const calendar = calendarRef.current?.getApi();
        if (calendar) {
          const hours = clickedDate.getHours();
          const adjustedHours = Math.max(8, hours - 1);
          
          // Calculate total duration for the appointment
          const totalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
          
          // Only scroll if should scroll based on appointment end time
          if (shouldScrollToAppointment(clickedDate, totalDuration)) {
          const timeStr = `${adjustedHours.toString().padStart(2, '0')}:00`;
          calendar.scrollToTime(timeStr);
          }
        }
      }, 100);
    }
  };

  // Handle event drop (drag and drop)
  const handleEventDrop = (dropInfo: any) => {
    // Only allow dragging for edit mode
    if (!isEdit) {
      return;
    }

    const { event } = dropInfo;
    const newStart = event.start;
    
    if (newStart) {
      // Update the appointment start time
      const dateStr = newStart.toISOString();
      onInputChange('date')({ target: { value: dateStr } });
      
      // Show success message
      toast.success(t('appointments.appointmentMoved'));
    }
  };

  // Handle calendar navigation
  const handlePrevClick = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.prev();
    }
  };

  const handleNextClick = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.next();
    }
  };

  const handleTodayClick = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.today();
    }
  };

  const handleViewChange = (view: string) => {
    setCalendarView(view);
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.changeView(view);
    }
  };


  return (
    <>
      <style>{timeoffStyles}</style>
      <Dialog 
          open={open} 
          onClose={onClose} 
          maxWidth="xl" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              margin: isMobile ? 0 : 'auto',
              maxHeight: isMobile ? '100vh' : '90vh',
              borderRadius: isMobile ? 0 : 1,
            }
          }}
          className={isMobile ? styles.dialogPaperMobile : styles.dialogPaper}
        >
        <DialogTitle className={styles.dialogTitle}>
          {title}
          <IconButton onClick={onClose} size="small" title={t('common.close')}>
            <Close />
          </IconButton>
        </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent className={isMobile ? styles.dialogContentMobile : styles.dialogContent}>
          <Box className={isMobile ? styles.mainContainerMobile : styles.mainContainer}>
            {/* Form Section */}
            <Box className={isMobile ? styles.formSectionMobile : styles.formSection}>
            {/* Basic Information */}
            <Box display="flex" flexDirection="column" gap={2}>
              <Box className={isMobile ? styles.formRowMobile : styles.formRow}>
                <Box flex={1}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(client) => client.name}
                    value={clients.find(client => client.id === formData.client_id) || null}
                    onChange={(event, newValue) => {
                      // Only allow client changes for new appointments or if not disabled
                      if (shouldDisableDateAndClient) {
                        return;
                      }
                      onInputChange('client_id')({ target: { value: newValue?.id || 0 } });
                      handleFieldBlur('client_id');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setClientSearchText(newInputValue);
                    }}
                      onBlur={() => handleFieldBlur('client_id')}
                    disabled={shouldDisableDateAndClient}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('common.client')}
                        required
                        error={shouldShowError('client_id', formData.client_id <= 0)}
                        helperText={shouldShowError('client_id', formData.client_id <= 0) ? t('forms.required') : ''}
                        placeholder={t('forms.selectClient')}
                        disabled={shouldDisableDateAndClient}
                      />
                    )}
                    renderOption={(props, client) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">{client.name}</Typography>
                          {client.phone_number && (
                            <Typography variant="body2" color="text.secondary">
                              {client.phone_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    noOptionsText={
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('clients.noClientsFound')}
                        </Typography>
                        {onCreateClient && !shouldDisableDateAndClient && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PersonAdd />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCreateClient(clientSearchText);
                            }}
                          >
                            {t('clients.createNewClient')}
                          </Button>
                        )}
                      </Box>
                    }
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    disableClearable={shouldDisableDateAndClient}
                  />
                </Box>
                <Box flex={1}>
                  <Autocomplete
                    options={hairdressers}
                    getOptionLabel={(hairdresser) => hairdresser.name}
                    value={hairdressers.find(hairdresser => hairdresser.id === formData.hairdresser_id) || null}
                    onChange={(event, newValue) => {
                      onInputChange('hairdresser_id')({ target: { value: newValue?.id || 0 } });
                      handleFieldBlur('hairdresser_id');
                    }}
                      onBlur={() => handleFieldBlur('hairdresser_id')}
                    disabled={shouldDisableInputs}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('common.hairdresser')}
                        required
                      error={shouldShowError('hairdresser_id', formData.hairdresser_id <= 0)}
                        helperText={shouldShowError('hairdresser_id', formData.hairdresser_id <= 0) ? t('forms.required') : ''}
                        placeholder={t('forms.selectHairdresser')}
                      />
                    )}
                    renderOption={(props, hairdresser) => (
                      <Box component="li" {...props}>
                        <Typography variant="body1">{hairdresser.name}</Typography>
                      </Box>
                    )}
                    noOptionsText={t('common.noOptions')}
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                  />
                </Box>
              </Box>


              <Box display="flex" gap={2}>
                <Box flex={1}>
                  <TextField
                    label={t('forms.appointmentStartDate')}
                    type="datetime-local"
                    value={formatDateForInput(formData.date)}
                    onChange={(e) => {
                      // Only allow date changes for new appointments or if not disabled
                      if (shouldDisableDateAndClient) {
                        return;
                      }
                      
                      const value = e.target.value;
                      const localDate = new Date(value);
                      const utcDate = localDate.toISOString();
                      onInputChange('date')({ target: { value: utcDate } });
                      
                      // Navigate calendar to the selected date and scroll to time
                      if (value && calendarRef.current) {
                        const selectedDate = new Date(value);
                        const calendar = calendarRef.current.getApi();
                        
                        // Check if we need to navigate to a different date/month
                        const currentDate = calendar.getDate();
                        const currentView = calendar.view;
                        const isSameDate = currentDate.toDateString() === selectedDate.toDateString();
                        const isSameMonth = currentDate.getMonth() === selectedDate.getMonth() && 
                                           currentDate.getFullYear() === selectedDate.getFullYear();
                        
                        // Navigate if:
                        // - Different date (for day/week views)
                        // - Different month (for month view)
                        const shouldNavigate = !isSameDate || 
                                             (currentView.type === 'dayGridMonth' && !isSameMonth);
                        
                        if (shouldNavigate) {
                          calendar.gotoDate(selectedDate);
                        }
                        
                        // Calculate scroll position based on selected time
                        const hours = selectedDate.getHours();
                        const minutes = selectedDate.getMinutes();
                        
                        // Calculate total duration for the appointment
                        const totalDuration = formData.services.reduce((total, service) => total + service.duration, 0);
                        
                        // Use scrollToTime with proper timing only if should scroll
                        setTimeout(() => {
                          const calendar = calendarRef.current?.getApi();
                          if (calendar && shouldScrollToAppointment(selectedDate, totalDuration)) {
                            // Subtract 1 hour offset from the selected time
                            const adjustedHours = Math.max(8, hours - 1); // Don't go below 8 AM (updated to match slotMinTime)
                            const timeStr = `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                            calendar.scrollToTime(timeStr);
                          }
                        }, isSameDate ? 200 : 600);
                      }
                    }}
                    onBlur={() => handleFieldBlur('date')}
                    fullWidth
                    required
                    error={shouldShowError('date', formData.date.trim() === '')}
                    helperText={shouldShowError('date', formData.date.trim() === '') ? t('forms.dateRequired') : ''}
                    disabled={shouldDisableDateAndClient}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      style: { 
                        padding: '16.5px 14px',
                        fontSize: '16px'
                      }
                    }}
                  />
                </Box>
                <div 
                  style={{
                    flex: 1,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                >
                  <TextField
                    label={t('forms.appointmentFinishDate')}
                    type="datetime-local"
                    value={calculateFinishTimeForInput()}
                    fullWidth
                    disabled={shouldDisableInputs}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& input': {
                          userSelect: 'none !important',
                          WebkitUserSelect: 'none !important',
                          MozUserSelect: 'none !important',
                          msUserSelect: 'none !important',
                          cursor: 'default',
                          backgroundColor: 'white',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderWidth: '1px',
                          }
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderWidth: '1px',
                          }
                        }
                      }
                    }}
                    inputProps={{
                      style: { 
                        padding: '16.5px 14px',
                        fontSize: '16px',
                        backgroundColor: 'white',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        cursor: 'default'
                      }
                    }}
                  />
                </div>
              </Box>

              {/* Status Field */}
              <Box>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={onInputChange('status')}
                      disabled={shouldDisableInputs || !isEdit}
                    >
                      <MenuItem value={AppointmentStatus.NEW}>{t('appointments.new')}</MenuItem>
                      <MenuItem value={AppointmentStatus.CONFIRMED}>{t('appointments.confirmed')}</MenuItem>
                      <MenuItem value={AppointmentStatus.COMPLETED}>{t('appointments.completed')}</MenuItem>
                      <MenuItem value={AppointmentStatus.CANCELED}>{t('appointments.canceled')}</MenuItem>
                    </Select>
                  </FormControl>
              </Box>
            </Box>

            <Divider />

            <TextField
              label={t('forms.appointmentNotes')}
              value={formData.notes || ''}
              onChange={onInputChange('notes')}
              fullWidth
              multiline
              rows={2}
              placeholder={t('forms.appointmentNotesPlaceholder')}
            />

            {/* Client Notes Display */}
            {formData.client_id > 0 && (
              <TextField
                label={t('forms.clientNotes')}
                value={clientNotes}
                onChange={handleClientNotesChange}
                fullWidth
                multiline
                rows={2}
                placeholder={t('forms.clientNotesPlaceholder')}
              />
            )}

            <Divider />

            {/* Service Templates Selection */}
            <ServiceTemplateSelector
              serviceTemplates={serviceTemplates}
              onAddService={onAddService}
                      disabled={shouldDisableInputs}
            />

            <Divider />

            {/* Selected Services */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('appointments.selectedServices', { count: formData.services.length })}
              </Typography>
              {formData.services.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('appointments.selectServiceTemplatesOrAddManually')}
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {formData.services.map((service, index) => (
                    <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2">
                          {t('appointments.service')} {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveService(index)}
                          color="error"
                          disabled={shouldDisableInputs}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" gap={2}>
                          <Box flex={1}>
                            <TextField
                              label={t('forms.serviceName')}
                              value={service.name}
                              onChange={(e) => onServiceChange(index, 'name', e.target.value)}
                              fullWidth
                              required
                              error={service.name.trim() === ''}
                              helperText={service.name.trim() === '' ? t('forms.serviceNameRequired') : ''}
                              disabled={shouldDisableInputs}
                            />
                          </Box>
                          <Box flex={1}>
                            <TextField
                              label={t('forms.serviceDescription')}
                              value={service.description || ''}
                              onChange={(e) => onServiceChange(index, 'description', e.target.value)}
                              fullWidth
                              disabled={shouldDisableInputs}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" gap={2}>
                          <Box flex={1}>
                            <Box display="flex" gap={1}>
                              <TextField
                                label={t('forms.hours')}
                                type="number"
                                value={Math.floor(service.duration / 60)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    // If field is empty, set to 0
                                    const minutes = service.duration % 60;
                                    onServiceChange(index, 'duration', minutes);
                                    return;
                                  }
                                  const hours = parseInt(value) || 0;
                                  const minutes = service.duration % 60;
                                  onServiceChange(index, 'duration', hours * 60 + minutes);
                                }}
                                fullWidth
                                required
                                inputProps={{ min: 0, max: 23 }}
                                error={service.duration <= 0}
                                disabled={shouldDisableInputs}
                              />
                              <TextField
                                label={t('forms.minutes')}
                                type="number"
                                value={service.duration % 60}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    // If field is empty, set to 0
                                    const hours = Math.floor(service.duration / 60);
                                    onServiceChange(index, 'duration', hours * 60);
                                    return;
                                  }
                                  const minutes = parseInt(value) || 0;
                                  const hours = Math.floor(service.duration / 60);
                                  onServiceChange(index, 'duration', hours * 60 + minutes);
                                }}
                                fullWidth
                                required
                                inputProps={{ min: 0, max: 59 }}
                                error={service.duration <= 0}
                                disabled={shouldDisableInputs}
                              />
                            </Box>
                            {(service.duration <= 0) && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {t('forms.durationGreaterThanZero')}
                              </Typography>
                            )}
                          </Box>
                          <Box flex={1}>
                            <TextField
                              label={t('forms.servicePrice')}
                              type="number"
                              value={service.price}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  onServiceChange(index, 'price', 0);
                                  return;
                                }
                                onServiceChange(index, 'price', Math.round(parseFloat(value) || 0));
                              }}
                              fullWidth
                              required
                              inputProps={{ min: 0, step: 1 }}
                              error={service.price <= 0}
                              helperText={service.price <= 0 ? t('forms.priceGreaterThanZero') : ''}
                              disabled={shouldDisableInputs}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Summary */}
            {formData.services.length > 0 && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body1">
                  <strong>{t('appointments.totalDuration')}:</strong> {formatDuration(calculateTotalDuration())}
                </Typography>
                <Typography variant="body1">
                  <strong>{t('appointments.totalPrice')}:</strong> {Math.round(calculateTotalPrice())} zł
                </Typography>
              </Box>
            )}
            </Box>

            {/* Calendar Section */}
            <Box flex={1} sx={{ minWidth: isMobile ? 300 : 400 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
                <Typography variant="h6">
                  {t('appointments.availableTimeSlots')}
                </Typography>
                
                {/* Navigation Controls */}
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton size="small" onClick={handlePrevClick}>
                    <ChevronLeft />
                  </IconButton>
                  <IconButton size="small" onClick={handleNextClick}>
                    <ChevronRight />
                  </IconButton>
                  <IconButton size="small" onClick={handleTodayClick} title={t('forms.goToToday')}>
                    <Today />
                  </IconButton>
                  
                  {/* View Switcher */}
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewChange('timeGridWeek')}
                      sx={{
                        backgroundColor: calendarView === 'timeGridWeek' ? 'primary.main' : 'transparent',
                        color: calendarView === 'timeGridWeek' ? 'white' : 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: '4px 0 0 4px',
                        '&:hover': {
                          backgroundColor: calendarView === 'timeGridWeek' ? 'primary.dark' : 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '10px' }}>
                        {t('calendar.week')}
                      </Typography>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewChange('timeGridDay')}
                      sx={{
                        backgroundColor: calendarView === 'timeGridDay' ? 'primary.main' : 'transparent',
                        color: calendarView === 'timeGridDay' ? 'white' : 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: '0 4px 4px 0',
                        borderLeftWidth: 0,
                        '&:hover': {
                          backgroundColor: calendarView === 'timeGridDay' ? 'primary.dark' : 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '10px' }}>
                        {t('calendar.day')}
                      </Typography>
                    </IconButton>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ height: isMobile ? 300 : 400, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FullCalendar
                  key={`calendar-${formData.date || 'new'}-${isEdit ? 'edit' : 'create'}`}
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView={isMobile ? 'timeGridDay' : calendarView}
                  initialDate={formData.date ? new Date(formData.date) : new Date()}
                  locale={plLocale}
                  headerToolbar={false}
                  events={allCalendarEvents}
                  dateClick={handleCalendarDateClick}
                  eventDrop={handleEventDrop}
                  datesSet={(dateInfo) => {
                    setCalendarRange((prev) => {
                      if (
                        prev &&
                        prev.start.getTime() === dateInfo.start.getTime() &&
                        prev.end.getTime() === dateInfo.end.getTime()
                      ) {
                        return prev;
                      }
                      return { start: dateInfo.start, end: dateInfo.end };
                    });
                  }}
                  height="100%"
                  slotMinTime="08:00:00"
                  slotMaxTime="20:00:00"
                  slotDuration="00:15:00"
                  slotLabelInterval="01:00:00"
                  allDaySlot={false}
                  firstDay={1}
                  dayMaxEvents={isMobile ? 2 : 3}
                  moreLinkClick="popover"
                  eventMaxStack={isMobile ? 1 : 3}
                  editable={isEdit}
                  eventResizableFromStart={false}
                  eventDurationEditable={false}
                  eventStartEditable={isEdit}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }}
                  slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }}
                  scrollTime="08:00:00"
                  scrollTimeReset={false}
                  eventContent={(eventInfo) => {
                    // Hide time display for timeoff events
                    if (eventInfo.event.extendedProps?.type === 'timeoff') {
                      return {
                        html: `<div class="fc-event-title"><strong>${t('common.timeOff')}:</strong> ${eventInfo.event.title}</div>`
                      };
                    }
                    // Use default rendering for other events (appointments)
                    return {
                      html: eventInfo.timeText ? 
                        `<div class="fc-event-time">${eventInfo.timeText}</div><div class="fc-event-title">${eventInfo.event.title}</div>` :
                        `<div class="fc-event-title">${eventInfo.event.title}</div>`
                    };
                  }}
                />
              </Box>

              {/* Timeoff Conflict Warning */}
              {timeoffConflict && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Typography variant="body2" sx={{ color: '#856404', fontWeight: 600, fontSize: '0.875rem' }}>
                    ⚠️ {t('appointments.timeoffConflictWarning')}
                  </Typography>
                </Box>
              )}

              {/* Payment Details Section - Only show for completed appointments */}
              {isEdit && formData.status === AppointmentStatus.COMPLETED && editingAppointment?.payments && editingAppointment.payments.length > 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon sx={{ fontSize: 20 }} />
                    {t('appointments.paymentDetails')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {editingAppointment.payments.map((payment: Payment, index: number) => (
                      <Box key={payment.id || index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {payment.method === 'cash' ? t('appointments.cash') : 
                             payment.method === 'card' ? t('appointments.card') : 
                             payment.method === 'voucher' ? t('appointments.voucher') : 
                             payment.method}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {parseFloat(payment.amount.toString()).toFixed(2)} zł
                        </Typography>
                      </Box>
                    ))}
                    
                    {/* Total Payment Summary */}
                    <Box sx={{ 
                      mt: 1, 
                      p: 1, 
                      backgroundColor: 'primary.light', 
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                          {t('appointments.totalPaid')}:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                          {editingAppointment.payments.reduce((sum: number, payment: Payment) => sum + parseFloat(payment.amount.toString()), 0).toFixed(2)} zł
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {isEdit && shouldDisableInputs && haveNotesChanged && (
            <Button 
              type="button"
              variant="contained" 
              onClick={handleNotesUpdate}
              color="primary"
            >
{t('forms.updateNotes')}
            </Button>
          )}
          <Button 
            type={isEdit && formData.status === AppointmentStatus.COMPLETED ? "button" : "submit"}
            variant="contained" 
            disabled={!isFormValid || (isEdit && !hasFormDataChanged) || (isEdit && shouldDisableInputs)}
            onClick={isEdit && formData.status === AppointmentStatus.COMPLETED ? handleFinalizeAppointment : undefined}
            color={isEdit && formData.status === AppointmentStatus.COMPLETED ? "success" : "primary"}
            startIcon={isEdit && formData.status === AppointmentStatus.COMPLETED ? <CheckCircle /> : undefined}
          >
            {isEdit && formData.status === AppointmentStatus.COMPLETED ? t('appointments.finalizeAppointment') : submitButtonText}
          </Button>
        </DialogActions>
      </form>
      </Dialog>
      
      {/* Finalize Appointment Modal */}
      {isEdit && editingAppointment && (
        <FinalizeAppointmentModal
          open={finalizeModalOpen}
          onClose={handleFinalizeModalClose}
          onFinalize={handleFinalize}
          appointment={{
            id: editingAppointment.id,
            client: editingAppointment.client || { name: 'Unknown Client' },
            hairdresser: editingAppointment.hairdresser || { name: 'Unknown Hairdresser' },
            date: editingAppointment.date,
            services: (editingAppointment.services || []).map((service: any) => ({
              name: service.name,
              price: parseFloat(service.price) || 0,
              duration: service.duration
            })),
            totalPrice: calculateTotalPrice(),
            totalDuration: calculateTotalDuration(),
          }}
        />
      )}
    </>
  );
};

export default AppointmentFormModal;
